import type {
  BrandItem,
  ColorItem,
  LensColorCategory,
  LensShowItem,
  LensShowResponse,
} from '@/app/_lens-components/types';
import { IS_US } from '@/utils/env/helper';
import { isMergeBrandDropdownLens } from './lensMergeBrand';
import { getLensColorCategoryViewModels, shouldShowLensColorCategories } from './lensColorCategory';

/** 镜片类型唯一键：折叠展开与 localStorage `prevLensType` 第一段均为 `${lens_type}_${lens_name}` */
export function getLensTypeKey(lens: LensShowItem): string {
  return `${lens.lens_type}_${lens.lens_name}`;
}

/** 记录当前镜片类型下选中的品牌，名称用于区分标题相同的品牌。 */
export function rememberLensBrandSelection(
  lens: LensShowItem,
  brand: BrandItem,
  shouldRememberBrandName = !IS_US,
): void {
  lens.default_brand_title = brand.brand_title;
  if (shouldRememberBrandName) {
    lens.default_brand_name = brand.brand_name;
  }
}

/** 按品牌名称区分同标题品牌，并兼容仅有默认品牌标题的接口数据。
 *  测试 sku: https://test-zeelool.xmslol.cn/lens-process?sku=ZOX006465-04
 * 【Z-用户侧】配镜流程增加默认勾选项（仅小语种站）-20260506
 *  https://alidocs.dingtalk.com/i/nodes/r1R7q3QmWe74xKoEFZmjPGaqJxkXOEP2?utm_scene=team_space
 */
export function resolveDefaultLensBrand(
  lens: LensShowItem,
  shouldRestoreBrandName = !IS_US,
): BrandItem | undefined {
  const exactBrand =
    shouldRestoreBrandName && lens.default_brand_name
      ? lens.brand_list?.find(
          (brand) =>
            brand.brand_title === lens.default_brand_title &&
            brand.brand_name === lens.default_brand_name,
        )
      : undefined;

  return (
    exactBrand ?? lens.brand_list?.find((brand) => brand.brand_title === lens.default_brand_title)
  );
}

export type ResolvedLensTypeColor = {
  lens: LensShowItem;
  brand: BrandItem | null;
  category: LensColorCategory | null;
  color: ColorItem;
};

/** 按「上次选中 key → 默认 key → 列表首项」解析选项 */
function pickLensOption<T>(
  list: T[],
  preferredKey: string,
  getKey: (item: T) => string | undefined,
  defaultKey?: string | null,
): T | undefined {
  if (!list.length) return undefined;
  const targetKey = preferredKey || defaultKey;
  if (targetKey) {
    const byTarget = list.find((item) => getKey(item) === targetKey);
    if (byTarget) return byTarget;
  }
  return list[0];
}

/**
 * 根据上次选择的镜片类型键 / 品牌 / 颜色，在 lens_show 列表中还原选中项。
 * `prev*` 全为空时，退化为列表第一项的默认品牌与颜色（与线上默认勾选兜底一致）。
 */
export function resolveLensTypeColorSelection(
  lensTypeColorData: LensShowResponse,
  prevLensTypeKey: string,
  prevLensBrandTitle: string,
  prevLensColor: string,
  prevLensBrandName = '',
  shouldRestoreBrandName = !IS_US,
): ResolvedLensTypeColor | null {
  if (!lensTypeColorData?.length) return null;

  const lens = prevLensTypeKey
    ? lensTypeColorData.find((item) => getLensTypeKey(item) === prevLensTypeKey)
    : lensTypeColorData[0];
  if (!lens) return null;

  const hasBrandDropdown = isMergeBrandDropdownLens(lens) && !!lens.brand_list?.length;
  const exactPreviousBrand =
    shouldRestoreBrandName && prevLensBrandName
      ? lens.brand_list?.find(
          (item) =>
            item.brand_title === prevLensBrandTitle && item.brand_name === prevLensBrandName,
        )
      : undefined;
  const brand = hasBrandDropdown
    ? exactPreviousBrand ||
      pickLensOption(
        lens.brand_list!,
        shouldRestoreBrandName && prevLensBrandName ? '' : prevLensBrandTitle,
        (b) => b.brand_title,
        lens.default_brand_title,
      ) ||
      null
    : null;

  if (shouldShowLensColorCategories(lens)) {
    const categories = getLensColorCategoryViewModels(lens);
    const categoryWithPreviousColor = prevLensColor
      ? categories.find((category) =>
          category.color_list?.some((colorItem) => colorItem.lens_color === prevLensColor),
        )
      : undefined;
    // 接口偶发空分类时，跳过它以保证默认勾选仍能落到一个可售颜色。
    const category = categoryWithPreviousColor ?? categories.find((item) => item.color_list.length);
    if (!category) return null;

    const color = prevLensColor
      ? category.color_list.find((colorItem) => colorItem.lens_color === prevLensColor)
      : undefined;
    const defaultColor = color ?? category.color_list[0];

    return defaultColor ? { lens, brand: null, category, color: defaultColor } : null;
  }

  const colorList = brand?.color_list || lens.color_list;
  if ((hasBrandDropdown && !brand?.color_list?.length) || !colorList?.length) return null;
  const defaultLensColor = brand?.default_lens_color || lens.default_lens_color;
  const color = pickLensOption(colorList, prevLensColor, (c) => c.lens_color, defaultLensColor);
  return color ? { lens, brand, category: null, color } : null;
}
